/* eslint-disable */
/**
 * In-memory stand-in for the Supabase JS client — LOCAL DEV ONLY.
 *
 * Only loaded when VITE_USE_MOCK === 'true' (see src/lib/supabase.js). Nothing
 * here runs in production, and no network calls are made.
 *
 * It implements the subset of the client this codebase actually uses, verified
 * by grepping src/: a chainable PostgREST-style query builder, PostgREST
 * embedded selects, the 10 rpc() functions, auth, and storage/realtime stubs.
 *
 * Mutations persist to localStorage so edits survive a reload while you work on
 * a screen. Call window.__mock.reset() to go back to the pristine seed.
 */

import { buildSeed, RELATIONS, CURRENT_USER_IDS } from './seed';

const DB_KEY = 'cmp:mockdb:v1';
const USER_KEY = 'cmp:mockuser:v1';

/**
 * Query instrumentation. Surfaces how many round-trips a screen actually makes
 * — useful for spotting per-row query patterns that will be slow against a real
 * Supabase too. Inspect with window.__mock.queryCount / .queryReport().
 */
const QUERY_STATS = { count: 0, byTable: {}, latency: 8 };

const deepClone = (v) =>
  typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v));

// ---------------------------------------------------------------------------
// storage-backed database
// ---------------------------------------------------------------------------

let db;

function loadDb() {
  try {
    const raw = localStorage.getItem(DB_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {
    /* corrupt or unavailable storage — fall through to a fresh seed */
  }
  const fresh = buildSeed();
  persist(fresh);
  return fresh;
}

function persist(next = db) {
  try {
    localStorage.setItem(DB_KEY, JSON.stringify(next));
  } catch (_) {
    /* quota exceeded — in-memory state still works for this session */
  }
}

db = loadDb();

const table = (name) => {
  if (!db[name]) db[name] = [];
  return db[name];
};

const newId = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

// ---------------------------------------------------------------------------
// select-string parsing (PostgREST syntax)
// ---------------------------------------------------------------------------

/** Split on commas that are not inside parentheses. */
function splitTopLevel(str) {
  const out = [];
  let depth = 0;
  let cur = '';
  for (const ch of str) {
    if (ch === '(') depth++;
    if (ch === ')') depth--;
    if (ch === ',' && depth === 0) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

/**
 * Parse `'*, user_profiles(first_name), conferences(name)'` into
 * { columns: ['*'], embeds: [{ name, select, inner, alias }] }.
 */
function parseSelect(sel) {
  const columns = [];
  const embeds = [];
  for (const part of splitTopLevel(sel || '*')) {
    const open = part.indexOf('(');
    if (open === -1) {
      const [alias, col] = part.includes(':') ? part.split(':') : [null, part];
      columns.push({ alias: alias || col.trim(), col: col.trim() });
      continue;
    }
    let head = part.slice(0, open).trim();
    const inner = part.slice(open + 1, part.lastIndexOf(')')).trim();
    let isInner = false;
    // Strip PostgREST hints: user_profiles!inner, user_profiles!fk_name
    if (head.includes('!')) {
      const [base, hint] = head.split('!');
      head = base.trim();
      if (hint.trim() === 'inner') isInner = true;
    }
    const [alias, name] = head.includes(':') ? head.split(':') : [null, head];
    embeds.push({ name: name.trim(), alias: (alias || name).trim(), select: inner, inner: isInner });
  }
  return { columns, embeds };
}

function resolveRelation(tableName, embedName) {
  const explicit = RELATIONS[tableName]?.[embedName];
  if (explicit) return explicit;
  // Fallback inference: <embed>_id on this row, else reverse <table>_id.
  const singular = embedName.replace(/s$/, '');
  return { table: embedName, localKey: `${singular}_id`, foreignKey: 'id' };
}

function project(tableName, row, parsed) {
  if (!row) return row;
  const wantsAll = parsed.columns.some((c) => c.col === '*');
  let out = wantsAll ? { ...row } : {};
  if (!wantsAll) {
    for (const { alias, col } of parsed.columns) out[alias] = row[col];
  }

  for (const embed of parsed.embeds) {
    const rel = resolveRelation(tableName, embed.name);
    const target = table(rel.table);

    if (rel.many) {
      const matches = target.filter((r) => r[rel.foreignKey] === row[rel.localKey]);
      // `table(count)` returns [{ count: n }] in PostgREST.
      if (embed.select.trim() === 'count') {
        out[embed.alias] = [{ count: matches.length }];
      } else {
        const sub = parseSelect(embed.select);
        out[embed.alias] = matches.map((m) => project(rel.table, m, sub));
      }
      if (embed.inner && matches.length === 0) return null;
    } else {
      const match = target.find((r) => r[rel.foreignKey] === row[rel.localKey]) || null;
      if (embed.inner && !match) return null;
      out[embed.alias] = match ? project(rel.table, match, parseSelect(embed.select)) : null;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// filters
// ---------------------------------------------------------------------------

const asArray = (v) => (Array.isArray(v) ? v : [v]);

const OPS = {
  eq: (a, b) => a === b || String(a) === String(b),
  neq: (a, b) => !(a === b || String(a) === String(b)),
  gt: (a, b) => a > b,
  gte: (a, b) => a >= b,
  lt: (a, b) => a < b,
  lte: (a, b) => a <= b,
  is: (a, b) => (b === null ? a === null || a === undefined : a === b),
  in: (a, b) => asArray(b).some((x) => x === a || String(x) === String(a)),
  like: (a, b) => new RegExp(`^${String(b).replace(/%/g, '.*')}$`).test(String(a ?? '')),
  ilike: (a, b) => new RegExp(`^${String(b).replace(/%/g, '.*')}$`, 'i').test(String(a ?? '')),
  cs: (a, b) => asArray(b).every((x) => (a || []).includes(x)),
};

// ---------------------------------------------------------------------------
// query builder
// ---------------------------------------------------------------------------

class MockQuery {
  constructor(tableName) {
    this.tableName = tableName;
    this.op = 'select';
    this.selectStr = '*';
    this.tests = [];
    this.orders = [];
    this.limitN = null;
    this.rangeArgs = null;
    this.mode = null; // 'single' | 'maybe'
    this.payload = null;
    this.countMode = null;
    this.headOnly = false;
    this.returning = true;
  }

  // -- shaping ---------------------------------------------------------------
  select(sel = '*', opts = {}) {
    this.selectStr = sel || '*';
    if (opts.count) this.countMode = opts.count;
    if (opts.head) this.headOnly = true;
    if (this.op !== 'select') this.returning = true;
    return this;
  }

  // -- mutations -------------------------------------------------------------
  insert(rows) {
    this.op = 'insert';
    this.payload = asArray(rows);
    this.returning = false;
    return this;
  }
  upsert(rows, opts = {}) {
    this.op = 'upsert';
    this.payload = asArray(rows);
    this.onConflict = opts.onConflict || 'id';
    this.returning = false;
    return this;
  }
  update(patch) {
    this.op = 'update';
    this.payload = patch;
    this.returning = false;
    return this;
  }
  delete() {
    this.op = 'delete';
    this.returning = false;
    return this;
  }

  // -- filters ---------------------------------------------------------------
  _push(col, op, val) {
    this.tests.push((row) => OPS[op](row[col], val));
    return this;
  }
  eq(c, v) { return this._push(c, 'eq', v); }
  neq(c, v) { return this._push(c, 'neq', v); }
  gt(c, v) { return this._push(c, 'gt', v); }
  gte(c, v) { return this._push(c, 'gte', v); }
  lt(c, v) { return this._push(c, 'lt', v); }
  lte(c, v) { return this._push(c, 'lte', v); }
  is(c, v) { return this._push(c, 'is', v); }
  in(c, v) { return this._push(c, 'in', v); }
  like(c, v) { return this._push(c, 'like', v); }
  ilike(c, v) { return this._push(c, 'ilike', v); }
  contains(c, v) { return this._push(c, 'cs', v); }
  match(obj) {
    Object.entries(obj || {}).forEach(([k, v]) => this._push(k, 'eq', v));
    return this;
  }
  not(col, op, val) {
    const fn = OPS[op] || OPS.eq;
    this.tests.push((row) => !fn(row[col], val));
    return this;
  }
  /** `.filter(col, op, val)` — PostgREST generic form. */
  filter(col, op, val) {
    const fn = OPS[op] || OPS.eq;
    this.tests.push((row) => fn(row[col], val));
    return this;
  }
  /** `.or('status.eq.draft,status.eq.sent')` */
  or(expr) {
    const clauses = splitTopLevel(expr).map((c) => {
      const [col, op, ...rest] = c.split('.');
      const raw = rest.join('.');
      const val = raw === 'null' ? null : raw;
      return (row) => (OPS[op] || OPS.eq)(row[col], val);
    });
    this.tests.push((row) => clauses.some((fn) => fn(row)));
    return this;
  }

  // -- ordering / paging -----------------------------------------------------
  order(col, { ascending = true } = {}) {
    this.orders.push({ col, ascending });
    return this;
  }
  limit(n) { this.limitN = n; return this; }
  range(from, to) { this.rangeArgs = [from, to]; return this; }
  single() { this.mode = 'single'; return this; }
  maybeSingle() { this.mode = 'maybe'; return this; }
  /** No-op: errors are returned in the result envelope, as with the real client. */
  throwOnError() { return this; }

  // -- execution -------------------------------------------------------------
  _matching(rows) {
    return rows.filter((r) => this.tests.every((t) => t(r)));
  }

  _run() {
    const rows = table(this.tableName);
    let result = [];
    const stamp = new Date().toISOString();

    if (this.op === 'insert' || this.op === 'upsert') {
      for (const raw of this.payload) {
        const row = { id: raw.id || newId(), created_at: stamp, updated_at: stamp, ...raw };
        const idx =
          this.op === 'upsert'
            ? rows.findIndex((r) => r[this.onConflict] === row[this.onConflict])
            : -1;
        if (idx >= 0) {
          rows[idx] = { ...rows[idx], ...row, updated_at: stamp };
          result.push(rows[idx]);
        } else {
          rows.push(row);
          result.push(row);
        }
      }
      persist();
    } else if (this.op === 'update') {
      const targets = this._matching(rows);
      targets.forEach((r) => Object.assign(r, this.payload, { updated_at: stamp }));
      result = targets;
      persist();
    } else if (this.op === 'delete') {
      const targets = this._matching(rows);
      db[this.tableName] = rows.filter((r) => !targets.includes(r));
      result = targets;
      persist();
    } else {
      result = this._matching(rows);
    }

    const total = result.length;

    // ordering
    for (const { col, ascending } of [...this.orders].reverse()) {
      result = [...result].sort((a, b) => {
        const x = a[col], y = b[col];
        if (x === y) return 0;
        if (x === null || x === undefined) return 1;
        if (y === null || y === undefined) return -1;
        return (x > y ? 1 : -1) * (ascending ? 1 : -1);
      });
    }

    if (this.rangeArgs) result = result.slice(this.rangeArgs[0], this.rangeArgs[1] + 1);
    if (this.limitN != null) result = result.slice(0, this.limitN);

    if (this.headOnly) return { data: null, error: null, count: total, status: 200 };

    const parsed = parseSelect(this.selectStr);
    let shaped = result.map((r) => project(this.tableName, deepClone(r), parsed)).filter(Boolean);

    if (this.mode === 'single') {
      if (shaped.length !== 1) {
        return {
          data: null,
          error: {
            code: 'PGRST116',
            message: `JSON object requested, multiple (or no) rows returned (${shaped.length})`,
            details: null,
          },
          count: total,
          status: 406,
        };
      }
      return { data: shaped[0], error: null, count: total, status: 200 };
    }
    if (this.mode === 'maybe') {
      return { data: shaped[0] ?? null, error: null, count: total, status: 200 };
    }
    return { data: shaped, error: null, count: this.countMode ? total : null, status: 200 };
  }

  // Thenable: `await supabase.from(...).select(...)` works unchanged.
  then(onFulfilled, onRejected) {
    // Keep loading states observable without making query-heavy screens crawl.
    // Screens that fire queries per row (see window.__mock.queryCount) were
    // taking ~20s at 60ms. Tune via window.__mock.latency.
    QUERY_STATS.count += 1;
    QUERY_STATS.byTable[this.table] = (QUERY_STATS.byTable[this.table] || 0) + 1;
    return new Promise((resolve) =>
      setTimeout(() => resolve(this._run()), QUERY_STATS.latency)
    ).then(onFulfilled, onRejected);
  }
  catch(fn) { return this.then((r) => r).catch(fn); }
  finally(fn) { return this.then((r) => r).finally(fn); }
}

// ---------------------------------------------------------------------------
// auth
// ---------------------------------------------------------------------------

const listeners = new Set();

function profileById(id) {
  return table('user_profiles').find((u) => u.id === id) || null;
}

function sessionFor(userId) {
  const p = profileById(userId);
  if (!p) return null;
  return {
    access_token: 'mock-access-token',
    refresh_token: 'mock-refresh-token',
    expires_in: 3600,
    expires_at: Math.floor(Date.now() / 1000) + 3600,
    token_type: 'bearer',
    user: {
      id: p.id,
      aud: 'authenticated',
      role: 'authenticated',
      email: p.email,
      email_confirmed_at: p.created_at,
      created_at: p.created_at,
      updated_at: p.updated_at,
      app_metadata: { provider: 'email' },
      user_metadata: { first_name: p.first_name, last_name: p.last_name, roles: p.roles },
    },
  };
}

let currentUserId = (() => {
  try {
    return localStorage.getItem(USER_KEY) || CURRENT_USER_IDS.admin;
  } catch (_) {
    return CURRENT_USER_IDS.admin;
  }
})();

let session = sessionFor(currentUserId);

function emit(event) {
  listeners.forEach((cb) => {
    try {
      cb(event, session);
    } catch (e) {
      console.error('[mock] auth listener error', e);
    }
  });
}

export function switchMockUser(userId) {
  currentUserId = userId;
  try { localStorage.setItem(USER_KEY, userId); } catch (_) {}
  session = sessionFor(userId);
  emit('SIGNED_IN');
}

const auth = {
  async getSession() { return { data: { session }, error: null }; },
  async getUser() { return { data: { user: session?.user ?? null }, error: null }; },
  onAuthStateChange(cb) {
    listeners.add(cb);
    // Real client fires asynchronously after subscribe; mirror that.
    setTimeout(() => cb('INITIAL_SESSION', session), 0);
    return {
      data: { subscription: { unsubscribe: () => listeners.delete(cb) } },
      error: null,
    };
  },
  async signInWithPassword({ email }) {
    const p = table('user_profiles').find(
      (u) => u.email.toLowerCase() === String(email || '').toLowerCase()
    );
    if (!p) {
      return { data: { user: null, session: null }, error: { message: 'Invalid login credentials' } };
    }
    switchMockUser(p.id);
    return { data: { user: session.user, session }, error: null };
  },
  async signUp({ email, options }) {
    const meta = options?.data || {};
    const row = {
      id: newId(),
      email,
      first_name: meta.first_name || 'New',
      last_name: meta.last_name || 'User',
      country: meta.country || 'Algeria',
      institution: meta.institution || 'Unknown',
      institution_type: meta.institution_type || 'university',
      department: meta.department || '',
      academic_rank: meta.academic_rank || 'other',
      roles: meta.roles?.length ? meta.roles : ['author'],
      subject_areas: meta.subject_areas || [],
      reviewer_keywords: meta.reviewer_keywords || [],
      available_as_reviewer: meta.available_as_reviewer !== false,
      receive_updates: !!meta.receive_updates,
      is_active: true,
      avatar_url: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    table('user_profiles').push(row);
    persist();
    switchMockUser(row.id);
    return { data: { user: session.user, session }, error: null };
  },
  async signOut() {
    session = null;
    emit('SIGNED_OUT');
    return { error: null };
  },
  async updateUser(attrs) {
    return { data: { user: { ...session?.user, ...attrs } }, error: null };
  },
  async resetPasswordForEmail() { return { data: {}, error: null }; },
  async exchangeCodeForSession() { return { data: { session }, error: null }; },
  async setSession() { return { data: { session }, error: null }; },
  async refreshSession() { return { data: { session }, error: null }; },
};

// ---------------------------------------------------------------------------
// rpc
// ---------------------------------------------------------------------------

const RPC = {
  get_my_profile: () => [profileById(currentUserId)].filter(Boolean),

  check_email_exists: ({ p_email, email }) => {
    const target = String(p_email || email || '').toLowerCase();
    return table('user_profiles').some((u) => u.email.toLowerCase() === target);
  },

  search_reviewers: ({ search_query }) => {
    const q = String(search_query || '').toLowerCase();
    return table('user_profiles')
      .filter((u) => u.available_as_reviewer && u.is_active)
      .filter((u) =>
        !q ||
        `${u.first_name} ${u.last_name} ${u.email} ${u.institution}`.toLowerCase().includes(q)
      );
  },

  get_my_pending_reviewer_invitations: () => {
    const me = profileById(currentUserId);
    if (!me) return [];
    return table('conference_invitations')
      .filter((i) => i.status === 'pending' && i.email.toLowerCase() === me.email.toLowerCase())
      .map((i) => {
        const conf = table('conferences').find((c) => c.id === i.conference_id);
        const inviter = profileById(i.invited_by);
        return {
          id: i.id,
          conference_id: i.conference_id,
          conference_name: conf?.name ?? null,
          conference_short_name: conf?.short_name ?? null,
          invited_by: i.invited_by,
          invited_by_name: inviter ? `${inviter.first_name} ${inviter.last_name}` : null,
          created_at: i.created_at,
        };
      });
  },

  accept_reviewer_invitation: ({ p_invitation_id }) => {
    const inv = table('conference_invitations').find((i) => i.id === p_invitation_id);
    if (!inv) return null;
    inv.status = 'accepted';
    table('conference_roles').push({
      id: newId(),
      conference_id: inv.conference_id,
      user_id: currentUserId,
      role: 'reviewer',
      created_at: new Date().toISOString(),
    });
    persist();
    return inv.conference_id;
  },

  publish_conference: ({ p_conference_id, p_target_status }) => {
    const c = table('conferences').find((x) => x.id === p_conference_id);
    if (!c) return null;
    c.status = p_target_status || 'published';
    c.billing_settled = true;
    persist();
    return { conference_id: c.id, status: c.status };
  },

  transfer_conference_ownership: ({ p_conference_id, p_new_owner }) => {
    const c = table('conferences').find((x) => x.id === p_conference_id);
    if (c) { c.created_by = p_new_owner; persist(); }
    return true;
  },

  approve_payment: ({ p_payment_id }) => {
    const p = table('payments').find((x) => x.id === p_payment_id);
    if (p) {
      p.status = 'approved';
      p.reviewed_by = currentUserId;
      p.reviewed_at = new Date().toISOString();
      persist();
    }
    return true;
  },

  reject_payment: ({ p_payment_id, p_note }) => {
    const p = table('payments').find((x) => x.id === p_payment_id);
    if (p) {
      p.status = 'rejected';
      p.admin_note = p_note ?? null;
      p.reviewed_by = currentUserId;
      p.reviewed_at = new Date().toISOString();
      persist();
    }
    return true;
  },

  get_or_create_conference_participant_number: ({ p_conference_id, p_user_id }) => {
    const rows = table('conference_participants');
    let row = rows.find((r) => r.conference_id === p_conference_id && r.user_id === p_user_id);
    if (!row) {
      row = {
        id: newId(),
        conference_id: p_conference_id,
        user_id: p_user_id,
        participant_number: rows.filter((r) => r.conference_id === p_conference_id).length + 1,
        created_at: new Date().toISOString(),
      };
      rows.push(row);
      persist();
    }
    return row.participant_number;
  },
};

/** rpc() results are thenable and support .single()/.maybeSingle() chaining. */
class MockRpc {
  constructor(name, args) {
    this.name = name;
    this.args = args || {};
    this.mode = null;
  }
  single() { this.mode = 'single'; return this; }
  maybeSingle() { this.mode = 'maybe'; return this; }
  select() { return this; }
  then(onFulfilled, onRejected) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const fn = RPC[this.name];
        if (!fn) {
          console.warn(`[mock] unimplemented rpc('${this.name}') — returning null`);
          return resolve({ data: null, error: null });
        }
        let data;
        try {
          data = fn(this.args);
        } catch (e) {
          return resolve({ data: null, error: { message: e.message } });
        }
        if (this.mode && Array.isArray(data)) data = data[0] ?? null;
        resolve({ data: data ?? null, error: null });
      }, 60);
    }).then(onFulfilled, onRejected);
  }
  catch(fn) { return this.then((r) => r).catch(fn); }
}

// ---------------------------------------------------------------------------
// storage + realtime stubs
// ---------------------------------------------------------------------------

const storage = {
  from(bucket) {
    return {
      async upload(path) {
        return { data: { path, fullPath: `${bucket}/${path}` }, error: null };
      },
      // Seeded avatars are inline data: URIs (keeps the mock fully offline), but
      // the app treats avatar_url as a storage path and signs it. Pass those
      // straight through instead of wrapping them in an unreachable URL.
      getPublicUrl(path) {
        if (typeof path === 'string' && path.startsWith('data:')) {
          return { data: { publicUrl: path } };
        }
        return { data: { publicUrl: `https://example.org/mock/${bucket}/${path}` } };
      },
      async createSignedUrl(path) {
        if (typeof path === 'string' && path.startsWith('data:')) {
          return { data: { signedUrl: path }, error: null };
        }
        return { data: { signedUrl: `https://example.org/mock/${bucket}/${path}` }, error: null };
      },
      async remove() { return { data: [], error: null }; },
      async download() { return { data: new Blob(['mock file']), error: null }; },
      async list() { return { data: [], error: null }; },
    };
  },
};

const channelStub = () => {
  const ch = {
    on: () => ch,
    subscribe: (cb) => { cb?.('SUBSCRIBED'); return ch; },
    unsubscribe: () => Promise.resolve('ok'),
  };
  return ch;
};

// ---------------------------------------------------------------------------
// client
// ---------------------------------------------------------------------------

export const mockSupabase = {
  from: (name) => new MockQuery(name),
  rpc: (name, args) => new MockRpc(name, args),
  auth,
  storage,
  channel: channelStub,
  removeChannel: () => Promise.resolve('ok'),
  functions: {
    async invoke(name, opts) {
      console.info(`[mock] functions.invoke('${name}')`, opts?.body);
      return { data: { success: true }, error: null };
    },
  },
};

/* Dev console helpers.
 *
 * Gated on `import.meta.env.DEV` as well as `window`, and that second guard is
 * the important one. `lib/supabase.js` imports this module statically, so it is
 * bundled and EVALUATED in production whether or not mock mode is on — the
 * `USE_MOCK` check only decides which client gets exported, not which modules
 * run. The result on the live site was a console banner reading
 *
 *     [CMP] Mock backend active — no network, no production data.
 *
 * which is both alarming and false, plus a `window.__mock` global whose
 * `reset()` clears localStorage and reloads the page. Vite replaces
 * `import.meta.env.DEV` with `false` at build time, so this whole block is
 * dropped from the production bundle.
 *
 * Note the module itself is still bundled — roughly 40KB of seed data that
 * production never uses. Removing that needs a dynamic import and an async
 * client, which is a bigger change than belongs in a pre-release fix.
 */
if (import.meta.env?.DEV && typeof window !== 'undefined') {
  window.__mock = {
    db,
    users: CURRENT_USER_IDS,
    switchUser: switchMockUser,
    currentUserId: () => currentUserId,
    get queryCount() { return QUERY_STATS.count; },
    set latency(ms) { QUERY_STATS.latency = ms; },
    get latency() { return QUERY_STATS.latency; },
    resetQueryStats() { QUERY_STATS.count = 0; QUERY_STATS.byTable = {}; },
    queryReport() {
      const rows = Object.entries(QUERY_STATS.byTable).sort((a, b) => b[1] - a[1]);
      console.table(rows.map(([table, n]) => ({ table, queries: n })));
      console.info(`total queries: ${QUERY_STATS.count}`);
      return QUERY_STATS.count;
    },
    reset() {
      localStorage.removeItem(DB_KEY);
      localStorage.removeItem(USER_KEY);
      window.location.reload();
    },
  };
  console.info(
    '%c[CMP] Mock backend active — no network, no production data. window.__mock.reset() to reseed.',
    'color:#b45309;font-weight:600'
  );
}

export default mockSupabase;
